// actions.js
import _merge from 'lodash/merge'
import 'whatwg-fetch'
import Promise from 'promise-polyfill'
import { cloneDeep } from 'lodash/lang.js'
import altracConversions from 'altrac-conversions'

import {
  connectionIssues,
} from './errors'

import {
  getCustomer,
} from './user'

import { buildURL as _buildURL, getWithAuth as _getWithAuth, sendWithAuth as _sendWithAuth } from '../util/api'

// AppStore
import AppStore from '../stores/AppStore'

if (!window.Promise) {
  window.Promise = Promise
}

function alertSuccess () {
  alert('Saved Successfully')
}

function alertFailure () {
  alert('Save Failure')
}

// API message when the device is outside of the user groups (see api-web authorize middleware)
const DEVICE_ACCESS_DENIED_MESSAGE = 'User does not have access to this device'

/**
 * Handle 403 responses: the user is logged in but not allowed to see this content.
 * A device outside of the user groups is shown as a page message, anything else as a popup.
 * @param {*} response
 */
function handleForbidden (response) {
  return response.json()
    .catch(() => ({}))
    .then(({ message } = {}) => {
      if (message === DEVICE_ACCESS_DENIED_MESSAGE) {
        AppStore.data.errors.deviceAccessDenied = window.location.pathname
        AppStore.emitChange()
      } else {
        connectionIssues({ issue: 'Forbidden', error: message })
      }

      throw new Error(`connection issue: 403 ${message}`)
    })
}

/**
 * Handle responses containing http return code as 'status' in the returned object.
 * @param {*} response
 */
function handleResponse (response) {
  if (response.status === 200) {
    connectionIssues(null, 'Connected')
    return response.json()
  }

  const { status: code, statusText: error } = response

  if (code === 403) return handleForbidden(response)
  const issue = { issue: code === 401 ? 'Unauthorized' : 'Bad status code', error }
  connectionIssues(issue)

  throw new Error(`connection issue: ${code} ${error}`)
}

/**
 * Perform the normal action for a fetch error.
 * @param {*} error
 */
const handleError = (error) => {
  throw new Error(`Issue connecting to server: ${JSON.stringify(error)}`)
}

/**
 * Perform the action when a data fetching error occurs.
 * @param {*} error
 */
const handleDataError = (error) => connectionIssues({ issue: 'Error fetching data', error })

export function saveHistory (currentLocation) {
  AppStore.data.history.past.unshift(currentLocation)
  if (AppStore.data.history.past.length > 5) {
    AppStore.data.history.past.pop()
  }
  AppStore.emitChange()
}

export function saveHistoryList (list) {
  AppStore.data.history.list = list
  AppStore.emitChange()
}

const getWithAuth = _getWithAuth
const sendWithAuth = _sendWithAuth
const buildURL = _buildURL

const invokeCallback = (callback) => callback && callback(null, AppStore)

const defaultBrand = {
  name: 'altrac',
  display_name: 'altrac',
  css: '{body {color:#284047;background-color:#d9d9d9;}',
  sales_email: 'sales@altrac.io',
  sales_phone: '+1 510 248 4141',
  support_email: 'support@altrac.io',
  support_phone: '+1 510 248 4141',
  locale: { language: 'en', tempConv: 'f', timeZone: 'US/Los Angeles' },

  icon_png: 'https://app.altrac.io/images/app/logo_trueblack.png',
  icon_svg: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 62 67"><style>path,circle{stroke-width:3;stroke:#88A0A7;fill:none;}</style><g id="altrac-icon"><title>Altrac.io</title><path d="m7,63c0,0 -7,-19 -0.3,-39C13,5 57,4 57,4c0,0 6,29 -5,46C40,67 7,63 7,63z" style="stroke-width:6"/><path d="M10,61C47,17 47,17 47,17 v -0"/><path d="M38,28C37,19 37,19 37,19"/><path d="M47,28C38,28 38,28 38,28"/><path d="M44,36C31,36 31,36 31,36"/><path d="M40,45C24,45 24,45 24,45"/><path d="M32,52C18,52 18,52 18,52"/><path d="M31,35C29,23 29,23 29,23"/><path d="M24,44C21,29 21,29 21,29"/><path d="M18,52C14,37 14,37 14,37"/><circle cx="49" cy="15" r="2.75"/><circle cx="50" cy="28" r="2.75"/><circle cx="47" cy="36.5" r="2.75"/><circle cx="43" cy="45" r="2.75"/><circle cx="35" cy="52" r="2.75"/><circle cx="37" cy="17" r="2.75"/><circle cx="29" cy="21" r="2.75"/><circle cx="21" cy="27" r="2.75"/><circle cx="14" cy="35" r="2.75"/></g></svg>',
  banner_svg: '<svg width="100%" height="100%" preserveAspectRatio="xMinYMid meet" viewBox="0 0 1105.443 345" xmlns="http://www.w3.org/2000/svg"><g id="f"><path d="M487.307,251.7c-8.73,8.418-19.33,16.524-38.66,16.524-31.8,0-49.571-19.017-49.571-48.324,0-35.229,24.941-51.442,70.148-51.442,6.235,0,11.847.312,13.406.936v-13.719c0-11.223-5.3-17.459-19.953-17.459-16.835,0-32.424,6.236-44.895,14.965l-16.524-33.359c18.395-12.47,43.336-19.33,68.901-19.33,38.971,0,61.731,11.535,61.731,51.442v63.913c0,22.136,2.182,31.177,7.794,40.218l-41.153,12.471c-5.3-2.806-10.288-10.289-11.223-16.836ZM482.319,227.694v-32.736c-2.183-.311-5.3-.623-8.73-.623-21.512,0-28.371,7.482-28.371,22.759,0,13.094,5.3,20.576,18.083,20.576,9.353,0,14.965-4.988,19.018-9.977Z" fill="inherit"/><path d="M554.338,221.459V46.869l46.142-8.107c2.182,8.73,3.429,21.201,3.429,37.413v137.178c0,17.147,2.494,19.33,8.418,19.33,1.87,0,2.806-.311,4.365-.624l3.741,31.801c-6.236,2.495-12.783,4.365-24.941,4.365-35.854,0-41.154-18.706-41.154-46.765Z" fill="inherit"/><path d="M636.332,223.641v-86.36h-14.965v-33.672h14.965c0-16.211.935-31.177,2.182-40.218l47.077-6.859v47.077h28.683v33.672h-28.995v78.565c0,14.029,3.118,16.835,13.406,16.835,5.612,0,11.535-1.87,15.277-3.741l4.053,32.424c-11.847,5.612-23.071,7.171-36.165,7.171-31.489,0-45.518-13.095-45.518-44.895Z" fill="inherit"/><path d="M732.666,106.416l42.089-5.923c1.559,3.741,3.118,13.718,3.118,19.018,5.924-11.536,17.147-19.953,32.736-19.953,5.3,0,11.535.623,17.459,3.117l-7.482,43.336c-4.053-1.871-8.418-3.43-14.029-3.43-14.342,0-22.447,7.794-24.318,19.641v102.261h-49.571V106.416Z" fill="inherit"/><path d="M916.925,251.7c-8.73,8.418-19.33,16.524-38.66,16.524-31.8,0-49.571-19.017-49.571-48.324,0-35.229,24.941-51.442,70.148-51.442,6.235,0,11.847.312,13.406.936v-13.719c0-11.223-5.3-17.459-19.953-17.459-16.835,0-32.424,6.236-44.895,14.965l-16.524-33.359c18.395-12.47,43.336-19.33,68.901-19.33,38.971,0,61.731,11.535,61.731,51.442v63.913c0,22.136,2.182,31.177,7.794,40.218l-41.153,12.471c-5.3-2.806-10.288-10.289-11.223-16.836ZM911.937,227.694v-32.736c-2.183-.311-5.3-.623-8.73-.623-21.512,0-28.371,7.482-28.371,22.759,0,13.094,5.3,20.576,18.083,20.576,9.353,0,14.965-4.988,19.018-9.977Z" fill="inherit"/><path d="M976.785,184.359c0-53.313,27.747-84.49,77.319-84.49,19.641,0,35.854,4.988,46.453,13.094l-12.471,35.23c-9.041-5.924-16.524-10.289-28.371-10.289-18.706,0-31.177,13.095-31.177,45.207,0,33.983,12.471,47.388,31.177,47.388,12.159,0,19.641-4.365,28.682-10.912l12.471,35.229c-14.029,8.418-29.306,13.406-49.571,13.406-48.947,0-74.513-30.865-74.513-83.865Z" fill="inherit"/></g><path d="M107.613,44.833c-43.489,0-78.744,35.255-78.744,78.744v171.356h171.355c43.489,0,78.744-35.255,78.744-78.744V44.833H107.613ZM252.134,216.188c0,28.623-23.287,51.91-51.91,51.91h-121.184l40.572-43.605h48.368c1.636,4.605,4.289,8.827,7.848,12.385,6.5,6.499,15.037,9.749,23.574,9.749s17.074-3.25,23.574-9.749c6.297-6.297,9.764-14.669,9.764-23.574s-3.468-17.277-9.764-23.574c-12.999-12.999-34.149-12.999-47.148,0-4.334,4.334-7.327,9.652-8.772,15.45l-28.06-1.521,45.849-49.276c4.582,2.22,9.568,3.338,14.557,3.338,8.537,0,17.074-3.25,23.574-9.749,6.297-6.297,9.764-14.669,9.764-23.574s-3.468-17.277-9.764-23.574c-12.999-12.999-34.149-12.999-47.148,0-6.297,6.297-9.764,14.669-9.764,23.574,0,5.134,1.159,10.088,3.342,14.572l-48.618,45.279-1.501-27.708c5.419-1.472,10.538-4.322,14.785-8.569,6.296-6.297,9.764-14.669,9.764-23.574s-3.468-17.277-9.764-23.574c-12.999-12.999-34.149-12.999-47.148,0-6.297,6.297-9.764,14.669-9.764,23.574s3.468,17.277,9.764,23.574c3.65,3.65,7.942,6.274,12.516,7.874v48.284l-43.735,40.731v-121.287c0-28.623,23.287-51.91,51.91-51.91h144.521v144.521ZM110.496,136.271c-3.171,0-6.152-1.235-8.394-3.477-4.628-4.628-4.628-12.159,0-16.788,2.242-2.242,5.223-3.477,8.394-3.477s6.152,1.235,8.394,3.477c4.628,4.628,4.628,12.159,0,16.788-2.241,2.242-5.223,3.477-8.394,3.477ZM191.008,204.911c2.242-2.242,5.223-3.477,8.394-3.477s6.152,1.235,8.394,3.477c4.628,4.628,4.628,12.159,0,16.788-2.242,2.242-5.223,3.477-8.394,3.477s-6.152-1.235-8.394-3.477c-4.628-4.628-4.628-12.159,0-16.788ZM191.008,116.006c2.242-2.242,5.223-3.477,8.394-3.477s6.152,1.235,8.394,3.477c4.628,4.628,4.628,12.159,0,16.788-2.242,2.242-5.223,3.477-8.394,3.477s-6.152-1.235-8.394-3.477c-4.628-4.628-4.628-12.159,0-16.788Z" fill="#fdbd57"/></svg>',
}

export function getBrand (name, callback) {
  const url = buildURL(`/brands/${name}`)

  fetch(url)
    .then(
      handleResponse,
      (error) => {
        handleError(error)
        AppStore.data.brand = defaultBrand
        AppStore.emitChange()
      }
    )
    .then((data) => {
      if (data === null) AppStore.data.brand = defaultBrand
      else AppStore.data.brand = data
      AppStore.emitChange()
      invokeCallback(callback)
    })
    .catch((error) => {
      handleDataError(error)
      AppStore.data.brand = defaultBrand
      AppStore.emitChange()
    })
}

export function getDevice (deviceID, callback) {
  const url = buildURL(`/devices/${deviceID}`)

  fetch(url, getWithAuth())
    .then((response) => handleResponse(response), (error) => handleError(error))
    .then((data) => {
      AppStore.data.lookup[data.id] = data
      AppStore.data.lookupReady = true
      AppStore.emitChange()
      invokeCallback(callback)
    })
    .catch((error) => {
      console.debug(error)
    })
}

export function getSettingHistory (deviceID, offset, limit, from, to, callback) {
  const url = buildURL(`/devices/${deviceID}/settingsHistory`, {
    offset, limit, from, to,
  })

  fetch(url, getWithAuth())
    .then((response) => handleResponse(response), (error) => handleError(error))
    .then((data) => {
      AppStore.data.deviceSettingHistory.data = data
      AppStore.data.deviceSettingHistory.ready = true
      AppStore.emitChange()
      invokeCallback(callback)
    })
    .catch((error) => {
      console.debug(error)
    })
}

export function unloadSettingHistory () {
  AppStore.data.deviceSettingHistory.data = []
  AppStore.data.deviceSettingHistory.ready = false
  AppStore.emitChange()
}

export function getDeviceOnAddress (address, callback) {
  const url = buildURL(`/devices/address/${address}`)

  fetch(url, getWithAuth())
    .then((response) => handleResponse(response), (error) => handleError(error))
    .then((data) => {
      for (let i = data.length - 1; i >= 0; i -= 1) {
        AppStore.data.lookup[data[i].id] = data[i]
      }
      AppStore.data.lookupReady = true
      AppStore.emitChange()
      invokeCallback(callback)
    })
    .catch(handleDataError)
}

export function getDeviceRules (address, callback) {
  const url = buildURL(`/rules/address/${address}`)

  fetch(url, getWithAuth())
    .then((response) => handleResponse(response), (error) => handleError(error))
    .then((data) => {
      AppStore.data.rules.rules = data
      AppStore.data.rules.alertsReady = true
      AppStore.emitChange()
      invokeCallback(callback)
    })
    .catch(handleDataError)
}

export function unloadDeviceRules () {
  AppStore.data.rules.rules = []
  AppStore.data.rules.alertsReady = false
  AppStore.emitChange()
}

export function unloadGroups () {
  AppStore.data.groups.groups = []
  AppStore.data.groups.groupsReady = false
  AppStore.emitChange()
}

export function unloadGroupDevices () {
  AppStore.data.groupDevices.devices = []
  AppStore.data.groupDevices.ready = false
  AppStore.emitChange()
}

export function getDoc (docID, params, callback) {
  const url = buildURL(`/docs/${docID}`, params)

  AppStore.data.doc = undefined

  fetch(url, getWithAuth())
    .then((response) => handleResponse(response), (error) => handleError(error))
    .then((data) => {
      AppStore.data.doc = data
      AppStore.emitChange()
      invokeCallback(callback)
    })
    .catch(handleDataError)
}

export function getDocs (params, callback) {
  const url = buildURL('/docs', params)

  fetch(url, getWithAuth())
    .then((response) => handleResponse(response), (error) => handleError(error))
    .then((data) => {
      AppStore.data.docs.docs = data.items
      AppStore.data.docs.docsReady = true
      AppStore.emitChange()
      invokeCallback(callback)
    })
    .catch(handleDataError)
}

export function unloadDocs () {
  AppStore.data.docs.docs = []
  AppStore.data.docs.docsReady = false
  AppStore.emitChange()
}

export function getRule (ruleID, callback) {
  const url = buildURL(`/rules/${ruleID}`)

  fetch(url, getWithAuth())
    .then((response) => handleResponse(response), (error) => handleError(error))
    .then((data) => {
      AppStore.data.rules.rule = data
      AppStore.data.rules.ruleReady = true
      AppStore.emitChange()
      invokeCallback(callback)
    })
    .catch(handleDataError)
}

export function getRules (customerID, params, callback) {
  const url = buildURL(`rules/customers/${customerID}`, params)

  fetch(url, getWithAuth())
    .then((response) => handleResponse(response), (error) => handleError(error))
    .then((data) => {
      AppStore.data.rules.rules = data
      AppStore.data.rules.ruleReady = true
      AppStore.emitChange()
      invokeCallback(callback)
    })
    .catch(handleDataError)
}

export function getDevices (deviceType, deviceGroup, customerID, groupID, isActive, callback) {
  const url = buildURL(`/customers/${customerID}/devices`, {
    deviceType,
    deviceGroup,
    groupID,
    isActive,
  })

  fetch(url, getWithAuth())
    .then((response) => handleResponse(response), (error) => handleError(error))
    .then((data) => {
      AppStore.data.groupDevices = {}
      AppStore.data.groupDevices.devices = data
      AppStore.data.groupDevices.ready = true
      for (let i = 0, len = data.length; i < len; i += 1) {
        AppStore.data.lookup[data[i].id] = data[i]
      }
      AppStore.data.lookupReady = true
      AppStore.emitChange()
      invokeCallback(callback)
    })
    .catch(handleDataError)
}

export function getCustomerDevices (customerID, callback) {
  if (!customerID) return

  const url = buildURL(`/customers/${customerID}/devices`)
  fetch(url, getWithAuth())
    .then((response) => handleResponse(response), (error) => handleError(error))
    .then((data) => {
      AppStore.data.customer.devices = data
      AppStore.emitChange()
      invokeCallback(callback)
    })
    .catch(handleDataError)
}

export function getCustomerUsers (customerID, callback) {
  if (!customerID) return

  const url = buildURL(`/customers/${customerID}/users`)
  fetch(url, getWithAuth())
    .then((response) => handleResponse(response), (error) => handleError(error))
    .then((data) => {
      AppStore.data.userCustomers = data.items
      AppStore.data.userCustomersReady = true
      AppStore.emitChange()
      invokeCallback(callback)
    })
    .catch(handleDataError)
}

export function getSearchedCustomer (customerID, user_id, callback) {
  const url = buildURL(`/customers/${customerID}`, { user_id })

  fetch(url, {
    method: 'get',
    headers: {
      Authorization: `Bearer ${AppStore.auth.idToken}`,
      'Content-Type': 'application/json',
    },
  })
    .then((response) => {
      if (response.status === 200) {
        connectionIssues(null, 'Connected')
        return response.json()
      }
      throw new Error(`Bad status code: ${response.status}`)
    }, () => {
      throw new Error('Issue connecting to server')
    })
    .then((data) => {
      AppStore.data.searchedCustomer.data = data
      AppStore.data.searchedCustomer.ready = true
      AppStore.data.searchedGroups.groups = data.applications
      AppStore.data.searchedGroupsReady = true
      invokeCallback(callback)

      // Emit change
      AppStore.emitChange()

      // Trigger callback (from server)
      if (callback) {
        callback(null, AppStore)
      }
    })
    .catch((error) => {
      connectionIssues({ issue: 'Error fetching data', error })
      if (callback) {
        callback(error)
      }
    })
}

export function getReading (readingID, callback) {
  const url = buildURL(`/readings/${readingID}`)

  fetch(url, getWithAuth())
    .then((response) => handleResponse(response), (error) => handleError(error))
    .then((data) => {
      AppStore.data.reading = data
      AppStore.data.readingReady = true
      AppStore.emitChange()
      invokeCallback(callback)
    })
    .catch(handleDataError)
}

export function getDeviceReadings (dateBegin, dateEnd, address, convert, callback) {
  const url = buildURL(`/readings/address/${address}`, { dateBegin, dateEnd, convert })

  fetch(url, getWithAuth())
    .then((response) => handleResponse(response), (error) => handleError(error))
    .then((data) => {
      if (convert) AppStore.data.deviceReading = data
      else AppStore.data.deviceReading = data.Items
      AppStore.data.deviceReadingReady = true
      AppStore.emitChange()
      invokeCallback(callback)
    })
    .catch(handleDataError)
}

export function unloadReading () {
  AppStore.data.reading = {}
  AppStore.data.readingReady = false
  AppStore.emitChange()
}

export function changeReading (reading, readingID, update) {
  const url = buildURL(`/readings${update ? '/' + readingID : ''}`)

  fetch(url, sendWithAuth(reading, update))
    .then((response) => {
      if (response.status === 200) {
        alertSuccess()
        connectionIssues(null, 'Connected')
      } else {
        alertFailure()
        connectionIssues({ issue: `Bad status code: ${response.status}`, error: response.statusText })
      }
    }, handleError)
    .catch((error) => {
      alertFailure()
      connectionIssues({ issue: 'Error fetching data', error })
    })
}

export function getMetric (deviceID, gta, lta, callback) {
  const url = buildURL(`/devices/${deviceID}/readings`, { gta, lta })

  fetch(url, getWithAuth())
    .then((response) => handleResponse(response), (error) => handleError(error))
    .then((data) => {
      AppStore.data.metric = data
      AppStore.data.metricReady = true
      AppStore.emitChange()
      invokeCallback(callback)
    })
    .catch(handleDataError)
}

export function getMetricAddress (address, dateBegin, dateEnd, callback) {
  const url = buildURL(`/readings/address/${address}`, { dateBegin, dateEnd })
  fetch(url, getWithAuth())
    .then((response) => handleResponse(response), (error) => handleError(error))
    .then((data) => {
      AppStore.data.metric = data.Items
      AppStore.data.metricReady = true
      AppStore.emitChange()
      invokeCallback(callback)
    })
    .catch(handleDataError)
}

export function unloadMetric () {
  AppStore.data.metricReady = false
  if (AppStore.data.metric) {
    AppStore.data.metric.length = 0
  }
  AppStore.data.statsReady = 'notLoaded'
  AppStore.data.stats.length = 0
  AppStore.emitChange()
}

export function getMoistureSensorStats (deviceID, dateBegin, dateEnd, callback) {
  const url = buildURL(
    `/moistureSensorStats/${deviceID}`,
    { dateBegin, dateEnd }
  )

  AppStore.data.statsReady = 'notLoaded'
  // Ensure stats is always an array
  if (!Array.isArray(AppStore.data.stats)) {
    AppStore.data.stats = []
  } else {
    AppStore.data.stats.length = 0
  }
  AppStore.emitChange()

  fetch(url, getWithAuth())
    .then(handleResponse, handleError)
    .then((data) => {
      // API returns an object with a `series` array; the UI expects the series array directly.
      // Keep backwards compatibility if an array is returned.
      AppStore.data.stats = Array.isArray(data) ? data : (data && Array.isArray(data.series) ? data.series : [])
      AppStore.data.statsReady = 'loaded'
      AppStore.emitChange()
      invokeCallback(callback)
    })
    .catch((error) => {
      // Don't leave the UI stuck in a loading state if this request fails.
      handleDataError(error)
      // Ensure stats is an array even on error
      if (!Array.isArray(AppStore.data.stats)) {
        AppStore.data.stats = []
      }
      AppStore.data.statsReady = 'loaded'
      AppStore.emitChange()
      invokeCallback(callback)
    })
}

export function getPumpStats (deviceID, dateBegin, dateEnd, utcOffset, callback) {
  const url = buildURL(
    `/devices/${deviceID}/stats/pump`,
    { dateBegin, dateEnd, utcOffset }
  )

  if (AppStore.data.statsReady !== 'loaded') AppStore.data.statsReady = 'notLoaded'
  else AppStore.data.statsReady = 'loading'

  AppStore.data.stats.length = 0
  AppStore.emitChange()

  fetch(url, getWithAuth())
    .then(handleResponse, handleError)
    .then((data) => {
      AppStore.data.stats = data
      AppStore.data.statsReady = 'loaded'
      AppStore.emitChange()
      invokeCallback(callback)
    })
    .catch(handleError)
}

export function getValveStats (deviceID, dateBegin, dateEnd, utcOffset, callback) {
  const url = buildURL(
    `/devices/${deviceID}/stats/valve`,
    { dateBegin, dateEnd, utcOffset }
  )

  if (AppStore.data.statsReady !== 'loaded') AppStore.data.statsReady = 'notLoaded'
  else AppStore.data.statsReady = 'loading'

  AppStore.data.stats.length = 0
  AppStore.emitChange()

  fetch(url, getWithAuth())
    .then((response) => {
      if (response.status === 403) {
        AppStore.data.statsReady = 'forbidden'
        AppStore.emitChange()
        return Promise.reject(new Error('Forbidden'))
      }
      return handleResponse(response)
    }, handleError)
    .then((data) => {
      AppStore.data.stats = data
      AppStore.data.statsReady = 'loaded'
      AppStore.emitChange()
      invokeCallback(callback)
    })
    .catch((error) => {
      if (error?.message !== 'Forbidden') {
        handleError(error)
      }
    })
}

export function getCustomers (distributor_id, callback) {
  const per_page = 200
  const url = buildURL('/customers', { per_page, distributor_id })

  fetch(url, getWithAuth())
    .then((response) => handleResponse(response), (error) => handleError(error))
    .then((data) => {
      AppStore.data.customers.data = data
      AppStore.data.customers.ready = true
      AppStore.data.customers.changing = false
      AppStore.emitChange()
      if (callback) {
        callback(null, AppStore)
      }
    })
    .catch((error) => {
      connectionIssues({ issue: 'Error fetching data', error })
      if (callback) {
        callback(error)
      }
    })
}

export function getCustomerRelations (customer_id, relation, callback) {
  const per_page = 200
  const url = buildURL('/customerRelation', { per_page, customer_id, relation })

  fetch(url, getWithAuth())
    .then((response) => handleResponse(response), (error) => handleError(error))
    .then((data) => {
      AppStore.data.customers.data = data
      AppStore.data.customers.ready = true
      AppStore.data.customers.changing = false
      AppStore.emitChange()
      if (callback) {
        callback(null, AppStore)
      }
    })
    .catch((error) => {
      connectionIssues({ issue: 'Error fetching data', error })
      if (callback) {
        callback(error)
      }
    })
}

export function getStatsAddress (address, dateBegin, dateEnd, valueKey, callback) {
  const tzOffset = -((new Date()).getTimezoneOffset() / 60)

  const url = buildURL(`/readings/address/${address}/stats/daily/${valueKey}`, { dateBegin, dateEnd, tzOffset })

  AppStore.data.statsReady = AppStore.data.statsReady === 'loaded' ? 'loaded' : 'loading'
  AppStore.emitChange()
  fetch(url, getWithAuth())
    .then((response) => handleResponse(response), (error) => handleError(error))
    .then((data) => {
      const { daily_summary: dailySummary } = data
      AppStore.data.stats = dailySummary
      AppStore.data.statsReady = 'loaded'

      for (let i = 0, len = dailySummary.length; i < len; i += 1) {
        AppStore.data.statsLookup[dailySummary[i].hash] = dailySummary[i]
      }
      AppStore.emitChange()
      invokeCallback(callback)
    })
    .catch(handleDataError)
}

export function getStatsAddressFlow (address, dateBegin, dateEnd, valueKey, callback) {
  const tzOffset = -((new Date()).getTimezoneOffset() / 60)

  const url = buildURL(`/readings/address/${address}/stats/flow`, { dateBegin, dateEnd, tzOffset })

  AppStore.data.flowReady = AppStore.data.flowReady === 'loaded' ? 'loaded' : 'loading'
  AppStore.emitChange()
  fetch(url, getWithAuth())
    .then((response) => handleResponse(response), (error) => handleError(error))
    .then((data) => {
      AppStore.data.flowStats = {}
      AppStore.data.flowStats = data
      AppStore.data.flowReady = 'loaded'

      // for (let i = 0, len = dailySummary.length; i < len; i += 1) {
      //   AppStore.data.statsLookup[dailySummary[i].hash] = dailySummary[i];
      // }
      AppStore.emitChange()
      invokeCallback(callback)
    })
    .catch((error) => {
      AppStore.data.flowReady = 'error'
      AppStore.emitChange()
      handleDataError(error)
    })
}

export function unloadFlowStats () {
  AppStore.data.flowStats = {}
  AppStore.data.flowReady = 'notLoaded'
  AppStore.emitChange()
}

export function invalidSetting (invalid) {
  if (invalid) {
    AppStore.data.deviceSettings.invalid = true
  } else {
    AppStore.data.deviceSettings.invalid = false
  }
  AppStore.emitChange()
}

export function changeSetting (setting) {
  // newSettings won't be changed in the database if newPhysical is also present.
  // AppStore.data.deviceSettings.newPhysical = undefined;
  AppStore.data.deviceSettings.newSettings = setting
  AppStore.emitChange()
}

export function changePhysical (physical, callback) {
  const { data: { deviceSettings: { newDeviceInfo: { physical: newPhysical } } } } = AppStore
  AppStore.data.deviceSettings.newDeviceInfo.physical = _merge(newPhysical, physical)
  invokeCallback(callback)
  AppStore.emitChange()
}
export function changeDevice (data) {
  AppStore.data.deviceSettings.newDeviceInfo = {
    ...AppStore.data.deviceSettings.newDeviceInfo,
    ...data,
  }
  AppStore.emitChange()
}

export function resetPopup () {
  AppStore.data.notifications.popUp.text = ''
  AppStore.data.notifications.popUp.visible = false
  if (AppStore.data.errors.connectionIssues.lastError?.issue === 'Forbidden') {
    AppStore.data.errors.connectionIssues.lastError = ''
  }
  AppStore.emitChange()
}

export function resetSettingsSavedStatus () {
  AppStore.data.deviceSettings.saved = false
  AppStore.emitChange()
}

export function resetNewSettings () {
  AppStore.data.deviceSettings.devices = []
  AppStore.data.deviceSettings.newSettings = []
  AppStore.data.deviceSettings.newDeviceInfo = {}
  resetSettingsSavedStatus()
}

export function initializeNewSettings (device) {
  AppStore.data.deviceSettings.newSettings = []
  AppStore.data.deviceSettings.newDeviceInfo.physical = typeof device === 'object' && !Array.isArray(device) && device.physical
    ? { ...device.physical }
    : {}
  resetSettingsSavedStatus()
}

export function selectDevice (id, state) {
  const idState = AppStore.data.deviceSettings.devices.indexOf(id)

  if (state) {
    if (idState === -1) {
      AppStore.data.deviceSettings.devices.push(id)
    }
  } else if (idState !== -1) {
    AppStore.data.deviceSettings.devices.splice(idState, 1)
  }

  AppStore.emitChange()
}

export function selectDevices (ids) {
  AppStore.data.deviceSettings.devices = []
  AppStore.data.deviceSettings.devices = AppStore.data.deviceSettings.devices.concat(ids)
  AppStore.emitChange()
}

export function selectDevicesRemove (callback) {
  AppStore.data.deviceSettings.devices = []
  AppStore.emitChange()
  invokeCallback(callback)
}

export function resetGroup () {
  AppStore.data.group = undefined
  AppStore.emitChange()
}

/**
 * Get group.
 * @param {int} groupID
 * @param {Object} params - boolean properties: includeDevices, includeUsers
 * @param {Func} callback - optional
 */
export function getGroup (groupID, params, callback) {
  const url = buildURL(`groups/${groupID}`, params)

  fetch(url, getWithAuth())
    .then((response) => handleResponse(response), (error) => handleError(error))
    .then((data) => {
      AppStore.data.group = data
      AppStore.emitChange()
      invokeCallback(callback)
    })
    .catch(handleDataError)
}

/**
 * Get groups.
 * @param {Object} params - boolean properties: includeDevices, includeUsers
 * @param {Func} callback - optional
 */
export function getGroups (params, callback) {
  const url = buildURL('groups', params)

  fetch(url, getWithAuth())
    .then((response) => handleResponse(response), (error) => handleError(error))
    .then((data) => {
      AppStore.data.groups = data.items
      AppStore.data.groupsReady = true
      AppStore.emitChange()
      invokeCallback(callback)
    })
    .catch(handleDataError)
}

/**
 * Store new group OR update existing group.
 * @param {Group} group - to store or update
 * @param {boolean} update - true updates existing group
 * @param {Func} callback - optional
 */
export function setGroup (group, update, callback) {
  const url = buildURL('groups')

  unloadGroups()

  fetch(url, sendWithAuth(group, update))
    .then((response) => handleResponse(response), (error) => handleError(error))
    .then((data) => {
      AppStore.data.groups.group = data
      AppStore.data.groups.groupsReady = true
      AppStore.emitChange()
      invokeCallback(callback)
    })
    .catch(handleDataError)
}

/**
 * Store new user_groups row OR update existing user_groups row.
 * @param {UserGroup} userGroup - to store or update
 * @param {boolean} update - true updates existing user_group
 * @param {Object} params - additional parameters
 * @param {Func} callback - optional
 */
export function setUserGroup (userGroup, update, params, callback) {
  const url = buildURL('userGroups', params)

  fetch(url, sendWithAuth(userGroup, update))
    .then((response) => handleResponse(response), (error) => handleError(error))
    .then((data) => {
      AppStore.data.userGroup = data
      if (Array.isArray(AppStore.data.userGroups) && data) {
        const index = AppStore.data.userGroups.findIndex((item) =>
          item.user_id === data.user_id && item.group_id === data.group_id)
        if (index >= 0) {
          AppStore.data.userGroups[index] = {
            ...AppStore.data.userGroups[index],
            ...data,
          }
        } else {
          AppStore.data.userGroups.push(data)
        }
      }
      AppStore.emitChange()
      invokeCallback(callback)
    })
    .catch((error) => {
      handleDataError(error)
      invokeCallback(callback, error)
    })
}

export function setSettings (settings, callback) {
  const url = buildURL('/settings')

  fetch(url, sendWithAuth(settings))
    .then((response) => {
      if (response.status === 200) {
        AppStore.data.deviceSettings.saved = true
        invokeCallback(callback)
        AppStore.emitChange()
        setTimeout(() => { resetSettingsSavedStatus() }, 1500)
        connectionIssues(null, 'Connected')
      } else {
        throw new Error(`Bad status code: ${response.status}`)
      }
    }, (error) => {
      throw new Error(`Issue connecting to server: ${JSON.stringify(error)}`)
    })
    .catch((error) => {
      AppStore.data.deviceSettings.saved = 'error'
      AppStore.emitChange()
      setTimeout(() => { resetSettingsSavedStatus() }, 1500)
      invokeCallback(callback, error)
      connectionIssues({ issue: 'Error fetching data', error })
    })
}

export function saveSettings (callback) {
  setSettings(AppStore.data.deviceSettings.newSettings, (error) => {
    if (error) {
      if (callback) { callback(error) }
    } else if (AppStore.data.deviceSettings.newSettings.address) {
      getDeviceOnAddress(AppStore.data.deviceSettings.newSettings.address, () => {
        invokeCallback(callback)
      })
    } else {
      invokeCallback(callback)
    }
  })
}

export function setDevice (deviceID, data, callback) {
  const url = buildURL(`/devices/${deviceID}`)
  resetSettingsSavedStatus()

  fetch(url, sendWithAuth(data, true))
    .then((response) => {
      if (response.status === 200) {
        return response.json()
      }
      throw new Error(`Bad status code: ${response.status}`)
    }, (error) => {
      throw new Error(`Issue connecting to server: ${JSON.stringify(error)}`)
    })
    .then((response) => {
      if (response.id) {
        AppStore.data.lookup[response.id] = {
          ...AppStore.data.lookup[response.id],
          ...response,
        }
        AppStore.data.lookupReady = true
        AppStore.data.deviceSettings.saved = true
        AppStore.emitChange()
      }
      setTimeout(() => { resetSettingsSavedStatus() }, 1500)
      invokeCallback(callback)
      connectionIssues(null, 'Connected')
    })
    .catch((error) => {
      console.error(error)
      AppStore.data.deviceSettings.saved = 'error'
      AppStore.emitChange()
      invokeCallback(callback, error)
      connectionIssues({ issue: 'Error updating device', error })
    })
}

export function swapModem (deviceID, data, callback) {
  const url = buildURL(`/devices/${deviceID}/modem`)
  resetSettingsSavedStatus()

  fetch(
    url,
    {
      method: 'post',
      body: JSON.stringify(data),
      headers: {
        Authorization: `Bearer ${AppStore.auth.idToken}`,
        'Content-Type': 'application/json',
      },
    }
  )
    .then((response) => {
      if (!response.ok) {
        return response.json()
          .then((body) => callback(body.message || 'error'))
          .catch(() => callback('error'))
      } else if (response.status === 200) {
        callback(null, response)
        return response.json()
      }
      throw new Error(`Bad status code: ${response.status}`)
    }, (error) => {
      throw new Error(`Issue connecting to server: ${JSON.stringify(error)}`)
    })
    .then((response) => {
      if (response && response.id) {
        AppStore.data.lookup[response.id] = {
          ...AppStore.data.lookup[response.id],
          ...response,
        }
        AppStore.data.lookupReady = true
        AppStore.data.deviceSettings.saved = true
        AppStore.emitChange()
        invokeCallback(callback)
      }
      setTimeout(() => { resetSettingsSavedStatus() }, 1500)
    })
    .catch(handleDataError)
}

export function setDevices (data, callback) {
  const url = buildURL('/devices')
  resetSettingsSavedStatus()

  fetch(url, sendWithAuth(data, true))
    .then((response) => {
      if (!response.ok) {
        callback('error')
      } else if (response.status === 200) {
        callback(null, response)
        return response.json()
      }
      throw new Error(`Bad status code: ${response.status}`)
    }, (error) => {
      throw new Error(`Issue connecting to server: ${JSON.stringify(error)}`)
    })
    .then((response) => {
      if (response.id) {
        AppStore.data.lookup[response.id] = {
          ...AppStore.data.lookup[response.id],
          ...response,
        }
        AppStore.data.lookupReady = true
        AppStore.data.deviceSettings.saved = true
        AppStore.emitChange()
        invokeCallback(callback)
      }
    })
    .catch(handleDataError)
}

export function setPhysical (deviceID, data, callback) {
  changePhysical(data)
  setDevice(deviceID, AppStore.data.deviceSettings.newDeviceInfo, () => {
    invokeCallback(callback)
  })
}

export function setRule (ruleID, rule, update, callback) {
  const url = buildURL(`/rules${update ? '/' + ruleID : ''}`)

  if (!ruleID || ruleID === 'new') delete rule.id

  fetch(url, sendWithAuth(rule, update))
    .then((response) => {
      if (response.status === 200) {
        AppStore.data.rules.saved = true
        AppStore.emitChange()
        connectionIssues(null, 'Connected')
        invokeCallback(callback)
      } else {
        throw new Error(`Bad status code: ${response.status}`)
      }
      return response.json()
    }, (error) => {
      throw new Error(`Issue connecting to server: ${JSON.stringify(error)}`)
    })
    .catch((error) => {
      AppStore.data.rules.saved = 'error'
      AppStore.emitChange()
      connectionIssues({ issue: 'Error saving rule', error })
      invokeCallback(callback, error)
    })
}

export function storeNewRule (rule, noEvent, callback) {
  AppStore.data.rules.newRule = cloneDeep(rule)
  if (!noEvent) AppStore.emitChange()
  invokeCallback(callback)
}

export function storeRule (rule, callback) {
  AppStore.data.rules.rule = cloneDeep(rule)
  AppStore.data.rules.saved = false
  AppStore.emitChange()
  invokeCallback(callback)
}

export function updateNewRuleBasic (address, active, nextNotification) {
  AppStore.data.rules.newRule.address = address
  AppStore.data.rules.newRule.active = active
  AppStore.data.rules.newRule.next_notification = nextNotification
  AppStore.emitChange()
}

export function updateNewRuleContactInfo (addresses, method, invalid) {
  AppStore.data.rules.newRule.transport.addresses = addresses
  AppStore.data.rules.newRule.transport.method = method
  AppStore.data.rules.invalidContactInfo = invalid
  AppStore.emitChange()
}

export function updateNewRuleParameters (
  template,
  test,
  formula,
  valueKey,
  testValue,
  multiplier,
  body,
  subject,
  debounceTime,
  sendOnce,
  notificationInterval,
  type,
  invalid,
  sensor
) {
  if (template) AppStore.data.rules.newRule.rule.template = template
  if (test) AppStore.data.rules.newRule.rule.test = test
  if (formula) AppStore.data.rules.newRule.rule.formula = formula
  if (sensor) AppStore.data.rules.newRule.rule.sensor = sensor
  if (valueKey) AppStore.data.rules.newRule.rule.valueKey = valueKey
  if (!!testValue || testValue === 0 || testValue === false) AppStore.data.rules.newRule.rule.testValue = testValue
  if (multiplier) AppStore.data.rules.newRule.rule.multiplier = multiplier
  if (body) AppStore.data.rules.newRule.transport.body = body
  if (subject) AppStore.data.rules.newRule.transport.subject = subject
  if (debounceTime) AppStore.data.rules.newRule.transport.debounceTime = debounceTime
  if (typeof sendOnce !== 'undefined') {
    AppStore.data.rules.newRule.transport.sendOnce = !!sendOnce
    if (!sendOnce) AppStore.data.rules.newRule.send_once_state = false
  }
  if (altracConversions.isNumber(notificationInterval)) {
    AppStore.data.rules.newRule.transport.notificationInterval = Number(notificationInterval)
  }
  if (type) AppStore.data.rules.newRule.type = type

  AppStore.data.rules.invalid = !!invalid
  AppStore.emitChange()
}

export function unloadRule () {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  AppStore.data.rules.ruleReady = false
  AppStore.data.rules.saved = false
  AppStore.data.rules.invalidContactInfo = true
  AppStore.data.rules.newRule = {
    active: 'true',
    address: '',
    next_notification: '0',
    send_once_state: false,
    rule: {},
    transport: {},
    type: '',
    recurrence: {
      schedule: 'none',
      timezone,
    }
  }
  AppStore.data.rules.rule = {}
  AppStore.emitChange()
}

export function unloadUserGroups () {
  AppStore.data.userGroups = undefined
  AppStore.emitChange()
}

export function setSort (direction, value1, value2) {
  AppStore.data.sort.direction = direction
  AppStore.data.sort.value1 = value1
  AppStore.data.sort.value2 = value2
  AppStore.emitChange()
}

export function switchCustomer (customerID, userID, selectedCompany, callback) {
  const url = buildURL('/users/customer/change')
  AppStore.data.customer.changing = true
  AppStore.data.customer.changingName = selectedCompany
  AppStore.emitChange()

  fetch(url, sendWithAuth({ userID, customerID }, false))
    .then((response) => handleResponse(response), (error) => handleError(error))
    .then((data) => {
      const {
        customer_id,
        permissions,
      } = data

      if (customer_id) {
        AppStore.data.customer_id = customer_id
        AppStore.data.user.customer_id = customer_id
        AppStore.data.user.permissions = permissions
        AppStore.emitChange()
      }
      invokeCallback(callback)
      connectionIssues(null, 'Connected')
      unloadGroups()
      unloadGroupDevices()
      getCustomer(customer_id)
    }, (error) => {
      throw new Error(`Issue connecting to server: ${JSON.stringify(error)}`)
    })
    .catch((error) => {
      AppStore.data.user.saved = 'error'
      AppStore.emitChange()
      invokeCallback(callback, error)
      connectionIssues({ issue: 'Error updating customers', error })
    })
}

export function getUserCustomer (userID, customerID, params, callback) {
  const url = buildURL(`/userCustomers/${userID}/${customerID}`, params)
  fetch(url, getWithAuth())
    .then((response) => handleResponse(response), (error) => handleError(error))
    .then((data) => {
      const { count, items } = data
      if (count) {
        const [userCustomer] = items
        AppStore.data.userCustomer = userCustomer
        AppStore.emitChange()
      }
      invokeCallback(callback)
    })
    .catch(handleDataError)
}

export function getUserGroups (params, callback) {
  const url = buildURL('/userGroups', params)

  fetch(url, getWithAuth())
    .then((response) => handleResponse(response), (error) => handleError(error))
    .then((data) => {
      const { items } = data
      AppStore.data.userGroups = items
      AppStore.emitChange()
      invokeCallback(callback)
    })
    .catch(handleDataError)
}

export function updateCustomer (customerID, data, callback) {
  const url = buildURL(`/customers/${customerID}`)

  fetch(url, sendWithAuth(data, true))
    .then((response) => {
      if (response.status === 200) {
        if (response.customer_name) {
          AppStore.data.customer.data = response
          AppStore.emitChange()
        }
        invokeCallback(callback)
        connectionIssues(null, 'Connected')
      } else {
        throw new Error(`Bad status code: ${response.status}`)
      }
    }, (error) => {
      throw new Error(`Issue connecting to server: ${JSON.stringify(error)}`)
    })
    .catch((error) => {
      AppStore.data.user.saved = 'error'
      AppStore.emitChange()
      invokeCallback(callback, error)
      connectionIssues({ issue: 'Error updating customers', error })
    })
}

export function createCustomer (payload, callback) {
  const url = buildURL('/customers')
  fetch(url, sendWithAuth(payload))
    .then((response) => {
      if (response.status === 200) {
        connectionIssues(null, 'Connected')
        return response.json()
      }
    })
    .then((data) => {
      if (callback) {
        callback(null, data)
      }
    })
    .catch((error) => {
      invokeCallback(callback, error)
      connectionIssues({ issue: 'Error creating customer', error })
    })
}

export function createRelation (payload, callback) {
  const url = buildURL('/customerRelation')
  fetch(url, sendWithAuth(payload))
    .then((response) => {
      if (response.status === 200) {
        connectionIssues(null, 'Connected')
        return response.json()
      }
    })
    .then((data) => {
      if (callback) {
        callback(null, data)
      }
    })
    .catch((error) => {
      invokeCallback(callback, error)
      connectionIssues({ issue: 'Error creating customer', error })
    })
}

export function createApiKey (payload, callback) {
  const url = buildURL('/applications')
  fetch(url, sendWithAuth(payload))
    .then((response) => {
      if (response.status === 200) {
        connectionIssues(null, 'Connected')
        return response.json()
      }
    })
    .then((data) => {
      callback(data)
    })
    .catch((error) => {
      invokeCallback(callback, error)
      connectionIssues({ issue: 'Error creating API key', error })
    })
}

export function deleteApiKey (application_id, callback) {
  const url = buildURL(`/applications/${application_id}`)
  fetch(
    url,
    {
      method: 'delete',
      headers: {
        Authorization: `Bearer ${AppStore.auth.idToken}`,
      },
    }
  )
    .then((response) => {
      if (response.status === 200) {
        connectionIssues(null, 'Connected')
        return response.json()
      }
      throw new Error(`Bad status code: ${response.status}`)
    })
    .catch((error) => {
      invokeCallback(callback, error)
      connectionIssues({ issue: 'Error deleting API key', error })
    })
}

export function getCustomerApiKeys (customerID, callback) {
  const url = buildURL(`/customer/${customerID}/applications`)
  fetch(url, getWithAuth())
    .then((response) => handleResponse(response), (error) => handleError(error))
    .then((data) => {
      AppStore.data.customerApiKeys.data = data
      AppStore.data.customerApiKeys.ready = true
      AppStore.emitChange()
      invokeCallback(callback)
    })
    .catch(handleDataError)
}

export function getUserEmail (email, includes, callback) {
  const url = buildURL(`/users/email/${email}?includes=user_customers`, includes)

  fetch(url, getWithAuth())
    .then((response) => {
      if (response.status === 200 || response.status === 404) {
        connectionIssues(null, 'Connected')
        return response.json()
      }
      throw new Error(`Bad status code: ${response.status}`)
    }, () => {
      throw new Error('Issue connecting to server')
    })
    .then((data) => {
      const {
        user_customers,
      } = data
      AppStore.data.searchedUser = []
      AppStore.data.searchedUser = user_customers
      AppStore.emitChange()

      // Trigger callback (from server)
      if (callback) {
        callback(null, AppStore)
      }
    })
    .catch((error) => {
      if (callback) {
        callback(error)
      }

      const errorStr = error.toString()

      if (errorStr === 'Error: Login Server Request Failed') {
        return connectionIssues({ issue: 'Unauthorized', error })
      }

      if (
        errorStr === 'Error: Server Error' ||
        errorStr === 'Error: Issue connecting to server'
      ) {
        return connectionIssues({ issue: 'Server Error', error })
      }

      return connectionIssues({ issue: 'Error fetching data', error })
    })
}

export function updateUserCustomer (userID, customerID, update, callback) {
  const url = buildURL(`/userCustomers/${userID}/${customerID}`)

  fetch(url, sendWithAuth(update, true))
    .then((response) => {
      if (response.status === 200) {
        AppStore.data.user.saved = true
        AppStore.emitChange()
        invokeCallback(callback)
        connectionIssues(null, 'Connected')
      } else {
        throw new Error(`Bad status code: ${response.status}`)
      }
    }, (error) => {
      throw new Error(`Issue connecting to server: ${JSON.stringify(error)}`)
    })
    .catch((error) => {
      AppStore.data.user.saved = 'error'
      AppStore.emitChange()
      invokeCallback(callback, error)
      connectionIssues({ issue: 'Error updating user_customers', error })
    })
}

export function loadDeviceStatus (status) {
  AppStore.data.deviceStatus = status
  AppStore.emitChange()
}

export function unloadDeviceStatus () {
  AppStore.data.deviceStatus = {}
  AppStore.emitChange()
}

export function login (email, password) {}

export function registerUser (email, password, profile) {}
